/**
 * The assign/unassign driver modal — the WHOLE modal (form, validation, dispatch
 * flow, event log) lives in the shared feature package; this shim injects only
 * what is dealer-specific: this app's generated query/mutation executors, the
 * per-user rideshare feature permission, identity, config, and Sentry. The
 * embed's sibling shim differs only in its auth model — that is the entire
 * sanctioned difference between the portals here.
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { useUser } from '@store';
import { Moves, Offers } from '@gql/base-types';
import {
  AssignOrUnassignMoveModalContent as SharedModalContent,
  type AssignModalHost,
  type AssignableDriver,
  type RankedDriverInfo,
} from '@hopdrive/feature-driver-assignment';
import { useGetDriversToAssignLazyQuery } from './queries/getDriversToAssign.query.generated';
import { useUpdateMoveTimesMutation } from '../../features/moves/queries/updateMoveTimes.mutation.generated';
import { useGetMoveForDispatchLazyQuery } from '../../features/moves/queries/getMoveForDispatch.query.generated';
import { useGetCustomerEffectiveConfigQuery } from '../../features/moves/queries/getCustomerEffectiveConfig.query.generated';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import config from '@config';

interface AssignOrUnassignMoveModalContentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (output?: any) => void;
  move?: Moves | null;
  offers?: Offers[] | null;
}

const AssignOrUnassignMoveModalContent = ({ onClose, move, offers }: AssignOrUnassignMoveModalContentProps) => {
  const user = useUser();
  const hasRideshareFeature = useFeaturePermission(FeatureName.DISPATCHING_RIDESHARE, move?.customer_id ?? undefined);

  const { data: customerConfigData, loading: configLoading } = useGetCustomerEffectiveConfigQuery({
    variables: { customerId: move?.customer_id ?? 0 },
    skip: !move?.customer_id,
    // Always read the live config — never serve a stale cached value.
    fetchPolicy: 'cache-and-network',
  });

  const [getDriversToAssign] = useGetDriversToAssignLazyQuery();
  const [updateMoveTimes] = useUpdateMoveTimesMutation();
  // network-only: the shared modal already runs this query on open, so a cache-first
  // executor would hand the submit-time refetch that same snapshot instead of fresh
  // child moves (the refetch exists to catch children added since the modal opened).
  const [getMoveForDispatch] = useGetMoveForDispatchLazyQuery({ fetchPolicy: 'network-only' });

  const host: AssignModalHost = React.useMemo(
    () => ({
      user: user ?? null,
      // Dispatch/undispatch netlify functions are served same-origin by this app.
      functionsOrigin: window.location.origin,
      userEmail: user?.profile?.email ?? null,
      hasRideshareFeature,
      rideshareEnabled: config.ridehail.enabled,
      effectiveConfig: customerConfigData?.customers_by_pk?.effective_config as Record<string, unknown> | undefined,
      effectiveConfigLoading: configLoading,
      getDriversToAssign: async () => {
        if (!move?.customer_id) return [];
        const res = await getDriversToAssign({ variables: { customerId: move.customer_id } });
        return (res?.data?.drivers ?? []) as AssignableDriver[];
      },
      // The lite-planning scorer runs server-side; this reaches it through this
      // app's own netlify function, authenticated with the caller's token (same
      // seam as the dispatch/undispatch calls above). A throw here is not fatal:
      // the picker drops to an A–Z list.
      getRankedDrivers: async request => {
        const token = await user?.getToken();
        if (!token) throw new Error('No auth token for driver ranking');
        const res = await fetch(`${window.location.origin}/.netlify/functions/rankDriversForAssign`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        });
        if (!res.ok) throw new Error(`Driver ranking failed (${res.status})`);
        const json = await res.json();
        if (!json?.success) throw new Error(json?.error || 'Driver ranking failed');
        return (json.rankedDrivers ?? []) as RankedDriverInfo[];
      },
      getMoveForDispatch,
      updateMoveTimes,
      onError: err => Sentry.captureException(err),
    }),
    [user, hasRideshareFeature, customerConfigData, configLoading, move?.customer_id, getDriversToAssign, getMoveForDispatch, updateMoveTimes]
  );

  return <SharedModalContent onClose={onClose} move={move} offers={offers} host={host} />;
};

export default AssignOrUnassignMoveModalContent;
